/**
 * Translates CDM format value objects into inline CSS for the editor CANVAS. A real gap found by
 * driving the editor manually and inspecting the actual rendered DOM: the toolbar correctly reads
 * and writes `RunFormat`/`ParagraphFormat`/`CellFormat` attrs, and the live preview (a real
 * server-rendered PDF) correctly reflects them — but schema.ts's `renderHTML` implementations
 * only ever did `mergeAttributes(HTMLAttributes)`, which computes no visual style at all from
 * those attrs. The canvas showed plain, unstyled text regardless of bold/italic/color/alignment/
 * etc., which defeats the point of a WYSIWYG editor for anything beyond block structure. These
 * functions are presentation-only — they have no bearing on fromCdm.ts/toCdm.ts or round-trip
 * fidelity, which already worked correctly off the underlying attrs, not off any rendered style.
 */
import { BorderStyle, HAlign, LineRule, TextFlow, UnderlineStyle, VAlign, VerticalScript, type BorderSide, type RunFormat, type ParagraphFormat, type CellFormat } from './types'

const BORDER_STYLE_CSS: Record<number, string> = {
  [BorderStyle.None]: 'none',
  [BorderStyle.Single]: 'solid',
  [BorderStyle.Double]: 'double',
  [BorderStyle.Dotted]: 'dotted',
  [BorderStyle.Dashed]: 'dashed',
  // Thick differs from Single only in width, which widthPt already carries.
  [BorderStyle.Thick]: 'solid',
}

/** A BorderSide as a CSS shorthand value, e.g. "1.5pt double #FF0000". */
function borderCss(side: BorderSide): string {
  const hex = side.colorHex.startsWith('#') ? side.colorHex : `#${side.colorHex}`
  return `${side.widthPt}pt ${BORDER_STYLE_CSS[side.style] ?? 'solid'} ${hex}`
}

export function runFormatToStyle(f: RunFormat): string {
  const parts: string[] = []
  if (f.bold) parts.push('font-weight:bold')
  if (f.italic) parts.push('font-style:italic')

  const decorationLines: string[] = []
  if (f.underline !== UnderlineStyle.None) decorationLines.push('underline')
  if (f.strike || f.doubleStrike) decorationLines.push('line-through')
  if (decorationLines.length) {
    parts.push(`text-decoration-line:${decorationLines.join(' ')}`)
    if (f.doubleStrike || f.underline === UnderlineStyle.Double) parts.push('text-decoration-style:double')
    else if (f.underline === UnderlineStyle.Dotted) parts.push('text-decoration-style:dotted')
    else if (f.underline === UnderlineStyle.Dashed) parts.push('text-decoration-style:dashed')
    if (f.underlineColorHex) parts.push(`text-decoration-color:${f.underlineColorHex}`)
  }

  if (f.colorHex) parts.push(`color:${f.colorHex}`)
  if (f.highlightHex) parts.push(`background-color:${f.highlightHex}`)
  else if (f.shadingHex) parts.push(`background-color:${f.shadingHex}`)
  if (f.fontFamily) parts.push(`font-family:${f.fontFamily}`)
  if (f.fontSizePt) parts.push(`font-size:${f.fontSizePt}pt`)
  if (f.script === VerticalScript.Superscript) parts.push('vertical-align:super', 'font-size:0.7em')
  if (f.script === VerticalScript.Subscript) parts.push('vertical-align:sub', 'font-size:0.7em')
  if (f.caps) parts.push('text-transform:uppercase')
  if (f.smallCaps) parts.push('font-variant:small-caps')
  if (f.letterSpacingPt) parts.push(`letter-spacing:${f.letterSpacingPt}pt`)
  return parts.join(';')
}

const H_ALIGN_CSS: Record<number, string> = { [HAlign.Left]: 'left', [HAlign.Center]: 'center', [HAlign.Right]: 'right', [HAlign.Justify]: 'justify' }

export function paragraphFormatToStyle(f: ParagraphFormat): string {
  const parts: string[] = [`text-align:${H_ALIGN_CSS[f.alignment] ?? 'left'}`]
  if (f.indentLeftPt) parts.push(`padding-left:${f.indentLeftPt}pt`)
  if (f.indentRightPt) parts.push(`padding-right:${f.indentRightPt}pt`)
  if (f.firstLineIndentPt) parts.push(`text-indent:${f.firstLineIndentPt}pt`)
  if (f.spaceBeforePt) parts.push(`margin-top:${f.spaceBeforePt}pt`)
  if (f.spaceAfterPt) parts.push(`margin-bottom:${f.spaceAfterPt}pt`)
  // LineRule.Exact/AtLeast both carry an absolute point size; CSS has no true "at least" line
  // height, so AtLeast is approximated the same way as Exact here (a floor, not a true minimum).
  if ((f.lineRule === LineRule.Exact || f.lineRule === LineRule.AtLeast) && f.lineHeightPt) {
    parts.push(`line-height:${f.lineHeightPt}pt`)
  } else if (f.lineHeight) {
    parts.push(`line-height:${f.lineHeight}`)
  }
  // Paragraph borders and shading DO get canvas styling — they are visible formatting the editor
  // should show. The four pagination flags deliberately get none: the canvas does not paginate,
  // so there is nothing for keepNext/keepLines/pageBreakBefore/widowControl to express there.
  if (f.borderTop) parts.push(`border-top:${borderCss(f.borderTop)}`)
  if (f.borderBottom) parts.push(`border-bottom:${borderCss(f.borderBottom)}`)
  if (f.borderLeft) parts.push(`border-left:${borderCss(f.borderLeft)}`)
  if (f.borderRight) parts.push(`border-right:${borderCss(f.borderRight)}`)
  if (f.shadingHex) parts.push(`background-color:${f.shadingHex.startsWith('#') ? f.shadingHex : `#${f.shadingHex}`}`)
  return parts.join(';')
}

const V_ALIGN_CSS: Record<number, string> = { [VAlign.Top]: 'top', [VAlign.Center]: 'middle', [VAlign.Bottom]: 'bottom' }

const TEXT_FLOW_CSS: Record<number, string> = {
  [TextFlow.Horizontal]: '',
  [TextFlow.RotateClockwise]: 'writing-mode:vertical-rl',
  [TextFlow.RotateCounterClockwise]: 'writing-mode:vertical-rl;transform:rotate(180deg)',
  [TextFlow.VerticalEastAsian]: 'writing-mode:vertical-rl;text-orientation:upright',
}

export function cellFormatToStyle(f: CellFormat): string {
  // Per-side margins since schema v14, each falling back to the uniform padding.
  const pt = f.paddingTopPt ?? f.padding
  const pr = f.paddingRightPt ?? f.padding
  const pb = f.paddingBottomPt ?? f.padding
  const pl = f.paddingLeftPt ?? f.padding
  const parts: string[] = [`vertical-align:${V_ALIGN_CSS[f.vAlign] ?? 'top'}`, `padding:${pt}pt ${pr}pt ${pb}pt ${pl}pt`]
  if (f.shadingHex) parts.push(`background-color:${f.shadingHex}`)
  // w:textDirection (schema v27) — the same CSS the HTML renderer writes, so the editor shows a
  // rotated header the way the output has it. writing-mode swaps the cell's own layout axes, which
  // is why the rotated text stays inside its column instead of merely looking rotated; CSS has no
  // bottom-to-top flow, so that one is vertical-rl turned through 180°.
  const flow = TEXT_FLOW_CSS[f.textFlow]
  if (flow) parts.push(flow)
  return parts.join(';')
}
